/**
 * TerminalDrawer — right-side панель с live-терминалом, открывается «как файл»
 * из любой DevOps-вкладки (по аналогии с FileEditor для файлов).
 *
 * Закрытие по X или клику снаружи. Внутри — `LiveTerminalPanel`, который сам
 * управляет WebSocket-соединением. Drawer демонтируется при закрытии (через
 * условный рендер), поэтому WS закрывается, и при следующем открытии терминал
 * стартует с чистого state — это намеренно (root-shell не должен случайно
 * висеть открытым).
 */

import { motion, AnimatePresence } from "framer-motion";
import { X, Maximize2, Minimize2 } from "lucide-react";
import { useEffect, useState } from "react";
import { LiveTerminalPanel } from "./LiveTerminalPanel";
import { cn } from "../../lib/cn";

interface TerminalDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function TerminalDrawer({ open, onClose }: TerminalDrawerProps) {
  const [maximized, setMaximized] = useState(false);

  // ESC закрывает
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="term-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[2px]"
            onClick={onClose}
            aria-label="Закрыть терминал"
          />
          <motion.aside
            key="term-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.2, ease: "easeOut" }}
            className={cn(
              "fixed top-0 right-0 z-[70] h-full bg-bg border-l border-border/60 shadow-2xl flex flex-col",
              maximized ? "w-full" : "w-full sm:w-[640px] md:w-[720px] lg:w-[820px]",
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/50 bg-bg-card/50">
              <span className="text-small font-semibold tracking-tight">Live-терминал</span>
              <span className="text-[10px] uppercase tracking-wider font-mono text-fg-subtle px-1.5 py-0.5 rounded-md bg-bg-subtle border border-border/40">
                root · host
              </span>
              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setMaximized((v) => !v)}
                  className="size-8 grid place-items-center rounded-lg text-fg-subtle hover:text-fg hover:bg-bg-subtle transition-colors"
                  aria-label={maximized ? "Свернуть" : "Развернуть"}
                  title={maximized ? "Свернуть" : "Развернуть"}
                >
                  {maximized ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="size-8 grid place-items-center rounded-lg text-fg-subtle hover:text-fg hover:bg-bg-subtle transition-colors"
                  aria-label="Закрыть"
                  title="Закрыть (Esc)"
                >
                  <X className="size-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-4">
              <LiveTerminalPanel />
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
